import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, resolveImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/hooks/useFavorites";
import { formatMoney, currencySymbol, initials } from "@/lib/format";
import { StarRating } from "@/components/StarRating";
import { MapEmbed } from "@/components/MapEmbed";
import { PageLoader, ErrorState } from "@/components/common";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Heart, MessageCircle, Phone, ShieldCheck, MapPin, Eye, Flag, Tag, AlertTriangle, Pencil, CheckCircle2,
  Maximize2, X, ChevronLeft, ChevronRight,
} from "lucide-react";

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { favoriteIds, toggleFavorite } = useFavorites();
  const [active, setActive] = useState(0);
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerAmt, setOfferAmt] = useState("");
  const [offerMsg, setOfferMsg] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [lightbox, setLightbox] = useState(false);

  const { data: listing, isLoading, isError, refetch } = useQuery({
    queryKey: ["listing", id],
    queryFn: () => apiFetch(`/listings/${id}`),
  });

  const { data: myOffer } = useQuery({
    queryKey: ["my-offer", id],
    queryFn: () => apiFetch(`/listings/${id}/my-offer`),
    enabled: !!user && !!listing && listing.seller_id !== user?.id,
  });

  const isOwner = user && listing && listing.seller_id === user.id;

  useEffect(() => {
    if (user && listing && !isOwner) apiFetch(`/listings/${id}/view`, { method: "POST" }).catch(() => {});
  }, [user, listing, isOwner, id]);

  const requireAuth = (msg) => {
    if (!user) {
      if (window.confirm(`${msg} Sign in now?`)) navigate("/login");
      return false;
    }
    return true;
  };

  const startChat = async () => {
    if (!requireAuth("Sign in to message the seller.")) return;
    try {
      const conv = await apiFetch(`/conversations?listing_id=${id}`, { method: "POST" });
      navigate(`/chat/${conv.id}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const callSeller = () => {
    if (!requireAuth("Sign in to see the seller's number.")) return;
    const phone = listing.seller?.phone;
    if (phone) window.location.href = `tel:${phone}`;
    else toast.error("Seller phone not available");
  };

  const onFav = () => { if (requireAuth("Sign in to save listings.")) toggleFavorite(id); };

  const sendOffer = async () => {
    try {
      await apiFetch(`/listings/${id}/offers`, { method: "POST", body: JSON.stringify({ amount: Number(offerAmt), message: offerMsg.trim() }) });
      toast.success("Offer sent!");
      setOfferOpen(false); setOfferAmt(""); setOfferMsg("");
      qc.invalidateQueries({ queryKey: ["my-offer", id] });
    } catch (err) { toast.error(err.message); }
  };

  const respondCounter = async (accept) => {
    try {
      await apiFetch(`/offers/${myOffer.id}/respond`, { method: "POST", body: JSON.stringify({ accept }) });
      toast.success(accept ? "Counter accepted" : "Counter declined");
      qc.invalidateQueries({ queryKey: ["my-offer", id] });
    } catch (err) { toast.error(err.message); }
  };

  const submitReport = async () => {
    try {
      await apiFetch(`/listings/${id}/report`, { method: "POST", body: JSON.stringify({ reason: reportReason.trim() }) });
      toast.success("Reported. Thanks for keeping Venda safe.");
      setReportOpen(false); setReportReason("");
    } catch (err) { toast.error(err.message); }
  };

  if (isLoading) return <PageLoader />;
  if (isError) return <ErrorState onRetry={refetch} />;

  const reduced = listing.original_price != null && listing.original_price > listing.price;
  const favorited = favoriteIds.includes(id);
  const images = listing.images || [];

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
        {/* Gallery */}
        <div className="lg:col-span-3">
          <div className="relative overflow-hidden rounded-2xl border border-border bg-secondary">
            {images[active] ? (
              <button type="button" onClick={() => setLightbox(true)} className="group block w-full cursor-zoom-in" data-testid="listing-main-image" aria-label="View image full screen">
                <img src={resolveImage(images[active])} alt={listing.title} className="aspect-[4/3] w-full object-cover transition group-hover:opacity-95" />
                <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-black/50 p-2 text-white opacity-0 transition group-hover:opacity-100">
                  <Maximize2 className="h-4 w-4" />
                </span>
              </button>
            ) : (
              <div className="aspect-[4/3] w-full" />
            )}
            {listing.sold && (
              <div className="absolute inset-0 flex items-center justify-center bg-brand/45">
                <span className="rotate-[-12deg] rounded-lg border-4 border-white px-6 py-2 text-3xl font-extrabold tracking-widest text-white">SOLD</span>
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto">
              {images.map((img, i) => (
                <button key={img} onClick={() => setActive(i)} className={`h-20 w-20 shrink-0 overflow-hidden rounded-lg border-2 ${i === active ? "border-price" : "border-transparent"}`}>
                  <img src={resolveImage(img)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-6 hidden lg:block">
            <h2 className="font-display text-lg font-bold">Description</h2>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">{listing.description || "No description provided."}</p>
            {listing.lat != null && <div className="mt-5 max-w-lg"><MapEmbed lat={listing.lat} lng={listing.lng} /></div>}
          </div>
        </div>

        {/* Details / actions */}
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium">{listing.condition}</span>
            <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium">{listing.category}</span>
            {reduced && <span className="flex items-center gap-1 rounded-full bg-price px-2.5 py-1 text-xs font-bold text-white"><Tag className="h-3 w-3" />Reduced</span>}
          </div>
          <h1 className="mt-3 font-display text-2xl font-extrabold text-foreground">{listing.title}</h1>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-price" data-testid="detail-price">{formatMoney(listing.price, listing.currency)}</span>
            {reduced && <span className="text-base text-muted-foreground line-through">{formatMoney(listing.original_price, listing.currency)}</span>}
          </div>
          <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{listing.location}{listing.area ? `, ${listing.area}` : ""}</span>
            <span className="flex items-center gap-1"><Eye className="h-4 w-4" />{listing.views_count || 0}</span>
            <span className="flex items-center gap-1"><Heart className="h-4 w-4" />{listing.favorites_count || 0}</span>
          </div>

          {/* Seller card */}
          {listing.seller && (
            <Link to={`/seller/${listing.seller.id}`} className="mt-5 flex items-center gap-3 rounded-2xl border border-border p-3 transition hover:bg-secondary" data-testid="seller-card">
              <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-brand text-sm font-bold text-white">
                {listing.seller.avatar_url ? <img src={resolveImage(listing.seller.avatar_url)} alt="" className="h-full w-full object-cover" /> : initials(listing.seller.name)}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-1 font-semibold">{listing.seller.name}{listing.seller.verified && <ShieldCheck className="h-4 w-4 text-sky-600" />}</div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <StarRating value={Math.round(listing.seller.rating_avg || 0)} size={12} />
                  {listing.seller.rating_count ? `${listing.seller.rating_avg} (${listing.seller.rating_count})` : "No ratings yet"}
                </div>
              </div>
            </Link>
          )}

          {/* Actions */}
          {isOwner ? (
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button onClick={() => navigate(`/edit/${id}`)} className="flex items-center justify-center gap-1.5 rounded-xl bg-brand py-2.5 text-sm font-semibold text-white" data-testid="edit-listing-btn"><Pencil className="h-4 w-4" />Edit</button>
              <button onClick={() => navigate(`/offers/${id}`)} className="rounded-xl border border-border py-2.5 text-sm font-semibold" data-testid="view-offers-btn">View offers</button>
            </div>
          ) : listing.sold ? (
            <div className="mt-5 rounded-xl bg-secondary py-3 text-center text-sm font-medium text-muted-foreground">This item has been sold.</div>
          ) : (
            <>
              <div className="mt-5 grid grid-cols-2 gap-2">
                <button onClick={startChat} className="flex items-center justify-center gap-1.5 rounded-xl bg-price py-3 text-sm font-semibold text-white transition hover:bg-price-light" data-testid="chat-btn"><MessageCircle className="h-4 w-4" />Chat</button>
                <button onClick={callSeller} className="flex items-center justify-center gap-1.5 rounded-xl bg-brand py-3 text-sm font-semibold text-white transition hover:bg-brand-light" data-testid="call-btn"><Phone className="h-4 w-4" />Call seller</button>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button onClick={onFav} className={`flex items-center justify-center gap-1.5 rounded-xl border py-2.5 text-sm font-semibold ${favorited ? "border-price text-price" : "border-border"}`} data-testid="save-btn">
                  <Heart className={`h-4 w-4 ${favorited ? "fill-price" : ""}`} />{favorited ? "Saved" : "Save"}
                </button>
                {listing.accepting_offers && (
                  <button onClick={() => requireAuth("Sign in to make an offer.") && setOfferOpen(true)} className="rounded-xl border border-border py-2.5 text-sm font-semibold" data-testid="offer-btn">Make offer</button>
                )}
              </div>

              {myOffer?.id && (
                <div className="mt-3 rounded-xl border border-border p-3 text-sm" data-testid="my-offer-status">
                  <p className="font-semibold">Your offer: {formatMoney(myOffer.amount, listing.currency)} · <span className="capitalize text-muted-foreground">{myOffer.status}</span></p>
                  {myOffer.status === "countered" && (
                    <div className="mt-2">
                      <p className="text-muted-foreground">Seller countered with {formatMoney(myOffer.counter_amount, listing.currency)}</p>
                      <div className="mt-2 flex gap-2">
                        <button onClick={() => respondCounter(true)} className="rounded-lg bg-price px-3 py-1.5 text-xs font-semibold text-white">Accept</button>
                        <button onClick={() => respondCounter(false)} className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold">Decline</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Safety note */}
          <div className="mt-5 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>Never pay before physically viewing the item. Venda Market is not liable for scams — meet in a safe, public place.</span>
          </div>

          {!isOwner && (
            <button onClick={() => requireAuth("Sign in to report.") && setReportOpen(true)} className="mt-3 flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-destructive" data-testid="report-btn">
              <Flag className="h-3.5 w-3.5" /> Report this listing
            </button>
          )}
        </div>

        {/* Mobile description */}
        <div className="lg:hidden">
          <h2 className="font-display text-lg font-bold">Description</h2>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">{listing.description || "No description provided."}</p>
          {listing.lat != null && <div className="mt-5"><MapEmbed lat={listing.lat} lng={listing.lng} /></div>}
        </div>
      </div>

      {/* Offer dialog */}
      <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Make an offer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{currencySymbol(listing.currency)}</span>
              <input data-testid="offer-amount" type="number" value={offerAmt} onChange={(e) => setOfferAmt(e.target.value)} placeholder="Your offer" className="w-full rounded-xl border border-input py-2.5 pl-8 pr-4 text-sm outline-none focus:border-price" />
            </div>
            <textarea data-testid="offer-message" value={offerMsg} onChange={(e) => setOfferMsg(e.target.value)} rows={3} placeholder="Message (optional)" className="w-full rounded-xl border border-input p-3 text-sm outline-none focus:border-price" />
          </div>
          <DialogFooter>
            <button onClick={sendOffer} disabled={!offerAmt} className="rounded-xl bg-price px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="send-offer-btn">Send offer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Report listing</DialogTitle></DialogHeader>
          <textarea data-testid="report-reason" value={reportReason} onChange={(e) => setReportReason(e.target.value)} rows={4} placeholder="What's wrong with this listing?" className="w-full rounded-xl border border-input p-3 text-sm outline-none focus:border-price" />
          <DialogFooter>
            <button onClick={submitReport} disabled={reportReason.trim().length < 3} className="flex items-center gap-1.5 rounded-xl bg-destructive px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="submit-report-btn"><CheckCircle2 className="h-4 w-4" />Submit report</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fullscreen image lightbox */}
      {lightbox && images[active] && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 animate-fade-up" onClick={() => setLightbox(false)} data-testid="image-lightbox">
          <button onClick={() => setLightbox(false)} className="absolute right-4 top-4 rounded-full bg-black/60 p-2.5 text-white ring-1 ring-white/50 transition hover:bg-black/80" data-testid="lightbox-close" aria-label="Close">
            <X className="h-6 w-6" />
          </button>
          {images.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); setActive((active - 1 + images.length) % images.length); }} className="absolute left-4 rounded-full bg-black/60 p-2.5 text-white ring-1 ring-white/50 transition hover:bg-black/80" data-testid="lightbox-prev" aria-label="Previous">
              <ChevronLeft className="h-7 w-7" />
            </button>
          )}
          <img src={resolveImage(images[active])} alt={listing.title} onClick={(e) => e.stopPropagation()} className="max-h-[90vh] max-w-[92vw] select-none object-contain" />
          {images.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); setActive((active + 1) % images.length); }} className="absolute right-4 rounded-full bg-black/60 p-2.5 text-white ring-1 ring-white/50 transition hover:bg-black/80" data-testid="lightbox-next" aria-label="Next">
              <ChevronRight className="h-7 w-7" />
            </button>
          )}
          {images.length > 1 && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/90">
              {active + 1} / {images.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
